import React from 'react';
import { observer } from 'mobx-react';
import { useTheme, Box, TextField, Tooltip, IconButton } from '@mui/material';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import ClearIcon from '@mui/icons-material/Clear';
import { TimeQcFieldName } from './const';
import useData from './hooks';
import { ValueFieldsTypesProps } from '50.quickConnect.Fields/FieldsTypes/types';
import QcTooltip from '50.quickConnect.Fields/QcTooltip';
import { FieldMainBox, FlexRow, FlexMainBoxRow } from '50.quickConnect.Fields/styles';
import { TimeDesc } from '90.quickConnect.Models/models';

const TimeQcField = ({
  field,
  updateDeclaration,
  labelSpan,
  setSelectedIndex,
  isReadOnly,
  labelClass,
}: ValueFieldsTypesProps) => {
  const timeDesc = field as TimeDesc;
  const { toolTip, label, id, min, max, fullPathId, isVertical } = timeDesc;
  const nameWithId = `${TimeQcFieldName}-${id}`;

  const {
    t,
    localValue,
    onChange,
    locale,
    handleOnChange,
    open,
    handleSelectField,
    handleOpenPicker,
    handlePickerClose,
    handleClear,
  } = useData(timeDesc, updateDeclaration, setSelectedIndex, isReadOnly);

  const { breakpoints } = useTheme();

  const isTestingMode =
    typeof navigator !== 'undefined' &&
    (navigator.webdriver ||
      typeof (window as Window & typeof globalThis & { Cypress?: unknown }).Cypress !== 'undefined');

  const labelBox = label ? (
    <Box sx={isVertical === false ? { ...FlexRow, flex: 2, width: 'auto' } : FlexRow}>
      <Box component="span" sx={{ flex: 1 }}>
        {labelSpan}
      </Box>

      {toolTip && <QcTooltip parentId={id} tooltip={toolTip} />}
    </Box>
  ) : (
    <></>
  );

  return (
    <Box
      className={labelClass}
      id={`scrollToAnchor-${fullPathId}`}
      sx={isVertical === false ? { ...FlexMainBoxRow(breakpoints), gap: 2 } : FieldMainBox(breakpoints)}
      data-cy={nameWithId}
      onClick={handleSelectField}
    >
      {labelBox}

      <Box sx={isVertical === false ? { flex: 1 } : {}}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
          <DesktopTimePicker
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                onBlur={handleOnChange}
                data-cy={`${nameWithId}-time`}
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    placeholder: 'HH:mm',
                    readOnly: !isTestingMode,
                    // En mode test, l'input est saisi directement : le picker ouvert masquerait les champs suivants
                    onClick: isTestingMode ? undefined : handleOpenPicker,
                  },
                }}
              />
            )}
            value={localValue}
            open={open}
            onOpen={handleOpenPicker}
            onClose={handlePickerClose}
            onChange={onChange}
            InputProps={{
              startAdornment: (
                <Tooltip title={t('formlib_array_deleted_title').toString()} style={{ order: 1 }}>
                  <span>
                    <IconButton data-cy={`${nameWithId}-clear`} edge="end" onClick={handleClear} disabled={isReadOnly}>
                      <ClearIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ),
            }}
            InputAdornmentProps={{
              position: 'end',
              style: { order: 2, marginLeft: 0 },
            }}
            minTime={min}
            maxTime={max}
            readOnly={isReadOnly}
            disabled={isReadOnly}
          />
        </LocalizationProvider>
      </Box>
    </Box>
  );
};

export default observer(TimeQcField);
